import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText, ShieldCheck, AlertCircle, CheckCircle2, Loader2, Lock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAX_CLASSIFICATIONS = [
  { v: 'individual_sole_prop', l: 'Individual / Sole Proprietor' },
  { v: 'single_member_llc',    l: 'Single-Member LLC' },
  { v: 'c_corporation',        l: 'C Corporation' },
  { v: 's_corporation',        l: 'S Corporation' },
  { v: 'partnership',          l: 'Partnership' },
  { v: 'trust_estate',         l: 'Trust / Estate' },
  { v: 'llc',                  l: 'Limited Liability Company (LLC)' },
];

const PublicW9Form = () => {
  const { token } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    legal_name: '', business_name: '', tax_classification: 'individual_sole_prop',
    address: '', city: '', state: '', zip: '',
    taxpayer_id: '', taxpayer_id_kind: 'ssn', signature: '', certify: false,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/w9/public/${token}`);
        setRequest(res.data);
        if (res.data.contractor_name && !form.legal_name) {
          setForm((f) => ({ ...f, legal_name: res.data.contractor_name }));
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'This link is invalid or has expired.');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.certify) { toast.error('You must certify the form'); return; }
    if (!form.signature.trim()) { toast.error('Signature required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/w9/public/${token}/submit`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const formatTin = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 9);
    if (form.taxpayer_id_kind === 'ssn') {
      if (d.length <= 3) return d;
      if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
      return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
    }
    // EIN XX-XXXXXXX
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}-${d.slice(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white border border-red-200 rounded-md p-8" data-testid="w9-public-error">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>This link cannot be opened</h1>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</p>
          <p className="text-xs text-slate-400 mt-4">Please contact the company that sent you this link to receive a new one.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white border border-emerald-200 rounded-md p-8" data-testid="w9-public-success">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Form Submitted</h1>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Thank you! Your W-9 has been securely transmitted to <strong>{request?.company_name}</strong>. They will review and notify you of approval.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        {/* Branded header */}
        <div className="bg-[#002FA7] text-white rounded-t-md p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-200">IRS Form W-9 · Secure Submission</p>
              <h1 className="text-2xl font-bold mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Complete your W-9 for {request?.company_name}
              </h1>
            </div>
            <ShieldCheck className="w-8 h-8 text-blue-200" />
          </div>
          <p className="text-sm text-blue-100 mt-3">
            Your taxpayer information is encrypted at rest. Only authorized personnel at {request?.company_name} can view it.
          </p>
        </div>

        {/* Rejection callout if applicable */}
        {request?.status === 'rejected' && request?.rejection_reason && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 -mt-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-1">Previous Submission Was Rejected</p>
            <p className="text-sm text-red-900">{request.rejection_reason}</p>
            <p className="text-xs text-red-600 mt-1">Please correct and resubmit.</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="bg-white rounded-b-md p-6 space-y-5" data-testid="w9-public-form">
          {/* Personal info */}
          <Section title="1. Identification">
            <Field label="Full Legal Name *">
              <input required value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="w9-legal-name" className={inputCls} />
            </Field>
            <Field label="Business Name / Disregarded Entity Name (if different)">
              <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="w9-business-name" className={inputCls} />
            </Field>
            <Field label="Federal Tax Classification *">
              <select required value={form.tax_classification} onChange={(e) => setForm({ ...form, tax_classification: e.target.value })} data-testid="w9-tax-class" className={`${inputCls} bg-white`}>
                {TAX_CLASSIFICATIONS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Field>
          </Section>

          <Section title="2. Address">
            <Field label="Street Address *">
              <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="w9-address" className={inputCls} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="City *">
                <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="w9-city" className={inputCls} />
              </Field>
              <Field label="State *">
                <input required maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} data-testid="w9-state" className={inputCls} placeholder="TX" />
              </Field>
              <Field label="ZIP *">
                <input required value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} data-testid="w9-zip" className={inputCls} />
              </Field>
            </div>
          </Section>

          <Section title="3. Taxpayer Identification Number" icon={Lock}>
            <p className="text-xs text-slate-500 mb-2">Enter either your Social Security Number (individuals) or Employer Identification Number (entities). This is encrypted before storage.</p>
            <div className="flex gap-3 mb-3">
              {[{ v: 'ssn', l: 'SSN' }, { v: 'ein', l: 'EIN' }].map((k) => (
                <label key={k.v} className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer ${form.taxpayer_id_kind === k.v ? 'border-[#002FA7] bg-blue-50' : 'border-slate-300'}`}>
                  <input type="radio" checked={form.taxpayer_id_kind === k.v} onChange={() => setForm({ ...form, taxpayer_id_kind: k.v, taxpayer_id: '' })} data-testid={`w9-tin-${k.v}`} />
                  <span className="text-sm font-semibold">{k.l}</span>
                </label>
              ))}
            </div>
            <Field label={`${form.taxpayer_id_kind.toUpperCase()} *`}>
              <input
                required
                value={form.taxpayer_id}
                onChange={(e) => setForm({ ...form, taxpayer_id: formatTin(e.target.value) })}
                data-testid="w9-taxpayer-id"
                placeholder={form.taxpayer_id_kind === 'ssn' ? '123-45-6789' : '12-3456789'}
                className={`${inputCls} font-mono`}
                inputMode="numeric"
              />
            </Field>
          </Section>

          <Section title="4. Certification">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-3 text-xs text-slate-700 space-y-1">
              <p className="font-bold">Under penalties of perjury, I certify that:</p>
              <p>1. The number shown on this form is my correct taxpayer identification number, and</p>
              <p>2. I am not subject to backup withholding, and</p>
              <p>3. I am a U.S. citizen or other U.S. person, and</p>
              <p>4. The FATCA code(s) entered on this form (if any) is correct.</p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer mb-3">
              <input
                type="checkbox" checked={form.certify}
                onChange={(e) => setForm({ ...form, certify: e.target.checked })}
                data-testid="w9-certify"
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700">I certify that the information above is true and correct.</span>
            </label>
            <Field label="Electronic Signature (type your full legal name) *">
              <input required value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} data-testid="w9-signature" placeholder="Type your full legal name" className={`${inputCls} font-mono italic`} />
            </Field>
          </Section>

          <button
            type="submit" disabled={submitting || !form.certify}
            data-testid="w9-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-md font-semibold disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Submit W-9 Securely
          </button>

          <p className="text-[10px] text-slate-400 text-center">
            Submission is encrypted in transit (TLS) and at rest (AES). Audit-logged. Powered by CivilX TMS.
          </p>
        </form>
      </div>
    </div>
  );
};

const inputCls = 'w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40';

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <section className="border-t border-slate-100 pt-4 first:border-0 first:pt-0">
    <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
      {Icon ? <Icon className="w-4 h-4 text-[#002FA7]" /> : null}
      {title}
    </h3>
    {children}
  </section>
);

export default PublicW9Form;
